import {
    DocumentViewer,
    ProviderDocumentUrl,
    ProviderMail,
    ProviderMailCart,
    ProviderMailCartShipment,
    renderAddressGroup,
    Spinner,
} from '@enter/core';
import { format } from 'date-fns';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { RootStore } from 'stores';
import * as S from './claim-timeline-table.styles';

interface IMailDetailProps {
    mail: ProviderMail;
    hideShipmentInformation?: boolean;
}

export const MailDetail: React.FunctionComponent<IMailDetailProps> = inject('rootStore')(
    observer(
        (props: IMailDetailProps): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');

            if (!rootStore) {
                return <></>;
            }
            const { claimStore } = rootStore;
            const { mail, hideShipmentInformation } = props;

            const [loading, setLoading] = useState(false);
            const [documentUrl, setDocumentUrl] = useState<ProviderDocumentUrl | undefined>();
            const [mailCart, setMailCart] = useState<ProviderMailCart | undefined>();

            useEffect(() => {
                (async (): Promise<void> => {
                    if (mail && mail.documentId) {
                        setLoading(true);
                        const url = await claimStore.loadDocumentDownloadUrl(mail.documentId);
                        setDocumentUrl(url);

                        // load mail cart
                        if (!hideShipmentInformation && mail.mailCartId) {
                            const cart = await claimStore.loadMailCart(mail.mailCartId);
                            setMailCart(cart);
                        }
                        setLoading(false);
                    }
                })();
            }, [mail, claimStore, hideShipmentInformation]);

            const renderDocument = () => {
                if (documentUrl?.url) {
                    return <DocumentViewer url={documentUrl.url} />;
                }
            };

            const renderMailCart = () => {
                if (mailCart) {
                    const items = _.map(mailCart?.items, item => renderMailCartItem(item));
                    return (
                        <>
                            <S.ExpandSegment />
                            <S.ExpandTable expand={true}>
                                <S.ExpandTBody>
                                    <S.ExpandTR>
                                        <S.ExpandTitleTH colSpan={2}> Mail Cart </S.ExpandTitleTH>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Name</S.ExpandTH>
                                        <S.ExpandTD>{mailCart.name}</S.ExpandTD>
                                    </S.ExpandTR>
                                    <S.ExpandTR>
                                        <S.ExpandTH>Items</S.ExpandTH>
                                        <S.ExpandTD>{items}</S.ExpandTD>
                                    </S.ExpandTR>
                                    {renderProviderMailCartShipment(mailCart.shipment)}
                                </S.ExpandTBody>
                            </S.ExpandTable>
                        </>
                    );
                }
            };
            const renderMailCartItem = (item: ProviderMail): React.ReactElement => (
                <div key={item.id}>
                    {item.receivedDate ? format(item.receivedDate, 'MMM dd, yyyy hh:mm') : 'Not received'} -{' '}
                    {item.externalId}
                </div>
            );

            const renderProviderMailCartShipment = (
                shipment?: ProviderMailCartShipment,
            ): React.ReactElement | undefined => {
                if (shipment) {
                    return (
                        <>
                            <S.ExpandTR>
                                <S.ExpandTH>From</S.ExpandTH>
                                <S.ExpandTD>{shipment.from}</S.ExpandTD>
                            </S.ExpandTR>
                            {shipment.to && (
                                <S.ExpandTR>
                                    <S.ExpandTH>To</S.ExpandTH>
                                    <S.ExpandTD>{renderAddressGroup([shipment.to])}</S.ExpandTD>
                                </S.ExpandTR>
                            )}
                            {shipment.method && (
                                <S.ExpandTR>
                                    <S.ExpandTH>Method</S.ExpandTH>
                                    <S.ExpandTD>
                                        {_.join(_.compact([shipment.method.name, shipment.method.estimate]), ', ')}
                                    </S.ExpandTD>
                                </S.ExpandTR>
                            )}
                            <S.ExpandTR>
                                <S.ExpandTH>Instructions</S.ExpandTH>
                                <S.ExpandTD>{shipment.instructions}</S.ExpandTD>
                            </S.ExpandTR>
                            <S.ExpandTR>
                                <S.ExpandTH>Tracking</S.ExpandTH>
                                <S.ExpandTD>
                                    {shipment.tracking?.courier}
                                    {shipment.tracking?.number}
                                    <br />
                                    <S.StyledLink href={shipment.tracking?.url}>Track Shipment</S.StyledLink>
                                </S.ExpandTD>
                            </S.ExpandTR>
                            <S.ExpandTR>
                                <S.ExpandTH>Weight</S.ExpandTH>
                                <S.ExpandTD>{shipment.weight} oz.</S.ExpandTD>
                            </S.ExpandTR>
                        </>
                    );
                }
            };

            return (
                <S.ExpandWrap key={mail.id}>
                    {loading ? (
                        <Spinner />
                    ) : (
                        <S.ExpandContainer>
                            {renderDocument()}
                            {!hideShipmentInformation && mailCart && renderMailCart()}
                        </S.ExpandContainer>
                    )}
                </S.ExpandWrap>
            );
        },
    ),
);
